/**
 * Keeps the app icon badge equal to the in-app unread count.
 *
 * The server sends the recipient's live unread count as `aps.badge` on every push, so
 * the icon is already correct on delivery. This hook owns the other half — the one the
 * reported bug was missing entirely: once the user has *read* something, nothing on iOS
 * lowers the badge on its own. Not opening the app, not tapping the notification, not
 * clearing Notification Center. Only an explicit write does.
 *
 * Three moments matter, and all three funnel into the same absolute write:
 *   1. the unread count changes in-app (read one, mark all read, delete, read a message)
 *   2. the app is resumed — the path that fixes the exact symptom reported, where a push
 *      arrived, was dealt with elsewhere, and the icon kept a number that is now wrong
 *   3. a push arrives while the app is in the foreground
 *
 * (2) and (3) do not write the badge themselves. They invalidate the two unread-count
 * queries; the refetch moves `unreadTotal`, and (1) does the write. One writer, no
 * chance of the badge and the header disagreeing.
 */

import { useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { App } from '@capacitor/app';
import type { PluginListenerHandle } from '@capacitor/core';
import { isNative } from '@/utils/platform';
import { setAppBadge } from '@/services/capacitor/appBadge';
import { addPushHandlers, clearAllNotifications } from '@/services/capacitor/pushNotifications';

interface UseAppBadgeOptions {
  /** Unread notifications + unread messages — the same sum the server badges with. */
  unreadTotal: number;
  /**
   * Only sync once the counts are real. Both queries default to 0 while in flight, and
   * syncing that would blank a correct badge on every cold start before restoring it.
   */
  enabled: boolean;
}

export function useAppBadge({ unreadTotal, enabled }: UseAppBadgeOptions): void {
  const queryClient = useQueryClient();

  // (1) The single writer.
  useEffect(() => {
    if (!enabled) return;

    void setAppBadge(unreadTotal);

    // Nothing is unread, so any banner still sitting in Notification Center is stale —
    // and on iOS a stale banner is exactly what re-asserts a badge the user already
    // dealt with. `Badge.set({ count: 0 })` clears them on iOS by itself; this is what
    // gives Android the same behaviour.
    if (unreadTotal === 0) {
      void clearAllNotifications();
    }
  }, [enabled, unreadTotal]);

  // (2) + (3) Re-read the counts; the effect above reacts to whatever comes back.
  useEffect(() => {
    if (!enabled) return;

    const refreshUnreadCounts = () => {
      void queryClient.invalidateQueries({ queryKey: ['unreadNotifications'] });
      void queryClient.invalidateQueries({ queryKey: ['unreadMessages'] });
    };

    // Subscribes to the one listener set owned by the push service — it never installs
    // listeners of its own. No-op on web, where there is nothing to subscribe to.
    const removePushHandlers = addPushHandlers({
      onNotificationReceived: refreshUnreadCounts,
    });

    // Native resume. On web the two queries already carry `refetchOnWindowFocus`, which
    // the WebView does not fire reliably — hence the explicit lifecycle listener here.
    let handle: PluginListenerHandle | undefined;
    let cancelled = false;
    if (isNative) {
      App.addListener('appStateChange', ({ isActive }) => {
        if (isActive) refreshUnreadCounts();
      }).then((h) => {
        if (cancelled) h.remove();
        else handle = h;
      });
    }

    return () => {
      cancelled = true;
      removePushHandlers();
      handle?.remove();
    };
  }, [enabled, queryClient]);
}

export default useAppBadge;
